import React, { useEffect, useMemo } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Stepper, { Step } from "../../components/@common/Stepper";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import PolicyInfoForm, { PolicyInfoFormProps } from "./PolicyInfoForm";
import PracticesInfoForm from "./PracticesInfoForm";
import PeopleInfoForm from "./PeopleInfoForm";
import { FormProvider } from "react-hook-form";

import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { gql } from "@apollo/client";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as PolicyIcon } from "../../@lib/icons/policy-icon.svg";
import { ReactComponent as PracticesIcon } from "../../@lib/icons/practices-icon.svg";
import { ReactComponent as PersonIcon } from "../../@lib/icons/person-icon.svg";
import { useApolloQuery, usePeoplesCall } from "../../hooks";
import { useAuthSession } from "../../components/AuthSessionProvider";
import { PersonNew, PracticeNew } from "../../pages";
import { ModalCloseIcon } from "../../@lib/icons";
import { Box, Modal } from "@mui/material";
import { ConditionalRenderer, useLanguageTranslator } from "../../components";
import { moduleKeys } from "../../utils/constants";

export type PolicyInputData = {
  title: string;
  description: string;
  date?: string;
  link?: string;
  policyCategoryId: string | number;
  themeId: string | number;
  countryId: string | number;
  geographicalAreaId: string | number;
};

export type PolicyEditorProps = {
  initialValues?: PolicyInputData;
  disabled?: boolean;
  onFinish: (policyData: PolicyInputData) => void;
  onCancel: () => void;
  imageAttachmentDetails?: any;
  documentAttachmentDetails?: any;
  setImageAttachmentDetails?: any;
  setDocumentAttachmentDetails?: any;
  isNotPracticeCreateModule: any;
  setSelectedListPractices?: any;
  selectedListPractices?: any;
  setSelectedListPeople?: any;
  selectedListPeople?: any;
  policyData?: any;
};

export type PolicyFormOptionsData = PolicyInfoFormProps;

const policyFormOptionsQueryDocument = gql`
  query policyFormOptionsQuery {
    countryOptions: countries {
      value: id
      label: name
    }
    themeOptions: themes {
      value: id
      label: title
    }
    geographicalAreaOptions: geographicalAreas {
      value: id
      label: name
      policyCategoryOptions: policyCategories {
        value: id
        label: name
      }
    }
    practiceOptions: practices {
      collection {
        value: id
        label: title
      }
    }
  }
`;

const style = {
  position: "absolute",
  margin: "2% 0px",
  width: "98%",
  left: "1%",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  overflow: "auto",
  height: "90dvh",
};

const PolicyEditor = (props: any) => {
  const [open, setOpen] = useState({ moduleKey: "", status: false });
  const [currStepIndex, setCurrStepIndex] = useState(0);

  const {
    initialValues,
    disabled,
    onFinish,
    onCancel,
    imageAttachmentDetails,
    documentAttachmentDetails,
    setImageAttachmentDetails,
    setDocumentAttachmentDetails,
    isNotPracticeCreateModule,
    setSelectedListPractices,
    selectedListPractices,
    setSelectedListPeople,
    selectedListPeople,
    policyData,
  } = props;

  const { user } = useAuthSession();
  const { translator } = useLanguageTranslator();

  const STEPS: Step[] = [
    {
      label: translator("Policy Information"),
      icon: <SvgIcon component={PolicyIcon} inheritViewBox />,
    },
    {
      label: translator("Practices"),
      icon: <SvgIcon component={PracticesIcon} inheritViewBox />,
    },
    {
      label: translator("People"),
      icon: <SvgIcon component={PersonIcon} inheritViewBox />,
    },
  ];
  const { data, loading, refetch } = useApolloQuery({
    queryDocument: policyFormOptionsQueryDocument,
  });

  const { data: peopleData } = usePeoplesCall({ isPrivate: true, user: user });

  const modifiedPeopleListData = peopleData?.people?.collection?.map(
    (item: any) => {
      const copyItem = { ...(item || {}) };
      return {
        label: `${copyItem?.firstname} ${copyItem?.lastname}` || "",
        value: copyItem?.id,
      };
    }
  );

  const totalSteps = !isNotPracticeCreateModule
    ? STEPS?.slice(0, 1).length
    : STEPS.length;
  const form = useForm<PolicyInputData>({
    defaultValues: {
      countryId: "",
      themeId: "",
      policyCategoryId: "",
      geographicalAreaId: "",
    },
    values: initialValues,
  });

  const geographicalAreaIdWatch = form.watch("geographicalAreaId");

  const categoryOptions = useMemo(() => {
    const geoZone = data?.geographicalAreaOptions?.find(
      (op: any) => op.value === form?.getValues()?.geographicalAreaId
    );
    return geoZone?.policyCategoryOptions || [];
  }, [geographicalAreaIdWatch, data?.geographicalAreaOptions]);

  /**
   * handleContinue()
   */
  const handleContinue = useCallback(async () => {
    // TODO: Implement step level validation by validating fields only related to current step.
    const isFormValid = await (() => {
      switch (currStepIndex) {
        case 0:
        case 1:
        case 2:
        default: {
          return form.trigger();
        }
      }
    })();

    if (isFormValid) setCurrStepIndex((currVal) => ++currVal);
    else void 0; // TODO: Implement Notification snackbar.
  }, [currStepIndex, form]);

  /**
   * handleBack()
   */
  const handleBack = useCallback(
    () => setCurrStepIndex((currVal) => --currVal),
    []
  );

  function handleClose() {
    setOpen({ moduleKey: "", status: false });
  }

  function onClickHandler(module: string) {
    setOpen({ moduleKey: module, status: true });
  }

  useEffect(() => {
    if (currStepIndex || currStepIndex === 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currStepIndex]);

  return (
    <div>
      <Container maxWidth="sm">
        <Stepper
          steps={!isNotPracticeCreateModule ? STEPS?.slice(0, 1) : STEPS}
          activeStep={currStepIndex}
        />
      </Container>

      <Divider sx={{ m: 4, opacity: 0.5 }} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onFinish)}>
          {currStepIndex === 0 && (
            <PolicyInfoForm
              themeOptions={data?.themeOptions || []}
              countryOptions={data?.countryOptions || []}
              geographicalAreaOptions={data?.geographicalAreaOptions || []}
              categoryOptions={categoryOptions || []}
              disabled={disabled || loading}
              data={policyData}
              imageAttachmentDetails={imageAttachmentDetails}
              setImageAttachmentDetails={setImageAttachmentDetails}
              documentAttachmentDetails={documentAttachmentDetails}
              setDocumentAttachmentDetails={setDocumentAttachmentDetails}
            />
          )}
          {currStepIndex === 1 && (
            <PracticesInfoForm
              data={data?.practiceOptions?.collection || []}
              disabled={loading}
              setSelectedListPractices={setSelectedListPractices}
              selectedListPractices={selectedListPractices}
              onClickHandler={() => onClickHandler(moduleKeys.practice)}
            />
          )}
          {currStepIndex === 2 && (
            <PeopleInfoForm
              data={modifiedPeopleListData || []}
              disabled={disabled || loading}
              state={selectedListPeople}
              setState={setSelectedListPeople}
              onClickHandler={() => onClickHandler(moduleKeys.people)}
            />
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            {currStepIndex === 0 && (
              <Button
                variant="contained"
                color="inherit"
                disabled={disabled}
                onClick={onCancel}
              >
                {translator("Cancel")}
              </Button>
            )}
            {currStepIndex > 0 && currStepIndex < totalSteps && (
              <Button
                variant="contained"
                color="inherit"
                disabled={disabled}
                onClick={handleBack}
              >
                {translator("support.Back")}
              </Button>
            )}
            {currStepIndex < totalSteps - 1 && (
              <Button
                variant="contained"
                disabled={disabled}
                onClick={handleContinue}
              >
                {translator("generic.Continue")}
              </Button>
            )}
            {currStepIndex === totalSteps - 1 && (
              <Button type="submit" variant="contained" disabled={disabled}>
                {translator("generic.Finish")}
              </Button>
            )}
          </Stack>
        </form>
      </FormProvider>
      <Modal
        open={open?.status}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack
            sx={{
              display: "flex",
              mt: 2,
              justifyContent: "end",
              alignItems: "end",
            }}
          >
            <Button
              onClick={handleClose}
              sx={{ padding: "0px", minWidth: "auto" }}
            >
              <ModalCloseIcon />
            </Button>
          </Stack>
          <ConditionalRenderer
            condition={open?.status && open?.moduleKey === moduleKeys.practice}
          >
            <PracticeNew
              isNotPolicyCreateModified={false}
              onClosePopup={handleClose}
              refetchFormOptions={refetch}
            />
          </ConditionalRenderer>
          <ConditionalRenderer
            condition={open?.status && open?.moduleKey === moduleKeys.people}
          >
            <PersonNew
              isNotPeopleCreateModified={false}
              onClosePopup={handleClose}
            />
          </ConditionalRenderer>
        </Box>
      </Modal>
    </div>
  );
};

export { PolicyEditor };
